import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  CardContent,
  TextField,
  Divider,
  Stack,
  IconButton,
  Grid
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import SecurityIcon from '@mui/icons-material/Security';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useNavigate, useLocation } from 'react-router-dom';
import { createSimpleOrder } from '../api/modules/payment';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormControl from '@mui/material/FormControl';
import { showMessage } from '../utils';
import { isInApp } from '../utils/environment';

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 2,
    backgroundColor: '#FFFFFF',
    height: 56,
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#007AFF',
      borderWidth: 1,
    },
  },
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E5E7EB',
  },
  '& .MuiOutlinedInput-input': {
    padding: '16px',
    fontSize: 15,
  },
  marginBottom: 12,
}));

const SecurityItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: 12,
}));

const PayCard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { couponTemplateListItem, methodType } = location.state || {};
  const [cardNumber, setCardNumber] = useState('');
  const [cardholderName, setCardholderName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [accountType, setAccountType] = useState('Checking');
  const [accountNumber, setAccountNumber] = useState('');
  const [routingNumber, setRoutingNumber] = useState('');

  // 格式化信用卡号码
  const handleCardNumberChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 16) value = value.slice(0, 16);
    
    // 添加空格分隔
    let formattedValue = '';
    for (let i = 0; i < value.length; i++) {
      if (i > 0 && i % 4 === 0) formattedValue += ' ';
      formattedValue += value[i];
    }
    
    setCardNumber(formattedValue);
  };

  // 格式化有效期
  const handleExpiryDateChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 4) value = value.slice(0, 4);
    
    if (value.length > 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    
    setExpiryDate(value);
  };

  // 格式化CVV
  const handleCvvChange = (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length > 3) value = value.slice(0, 3);
    setCvv(value);
  };

  // 渲染支付详情表单
  const renderPaymentForm = () => {
    if (methodType === 'payCardBO') {
      return (
        <Box sx={{ mb: 2 }}>
          <StyledTextField
            fullWidth
            placeholder="Card Number"
            variant="outlined"
            value={cardNumber}
            onChange={handleCardNumberChange}
            inputProps={{ maxLength: 19 }}
            sx={{ mb: 2 }}
            error={paymentError && !cardNumber}
          />
          
          <StyledTextField
            fullWidth
            placeholder="Cardholder Name"
            variant="outlined"
            value={cardholderName}
            onChange={(e) => setCardholderName(e.target.value)}
            sx={{ mb: 2 }}
            error={paymentError && !cardholderName}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                placeholder="MM/YY"
                variant="outlined"
                value={expiryDate}
                onChange={handleExpiryDateChange}
                inputProps={{ maxLength: 5 }}
                error={paymentError && !expiryDate}
              />
            </Grid>
            <Grid item xs={6}>
              <StyledTextField
                fullWidth
                placeholder="CVV"
                variant="outlined"
                value={cvv}
                onChange={handleCvvChange}
                type="password"
                inputProps={{ maxLength: 3 }}
                error={paymentError && !cvv}
              />
            </Grid>
          </Grid>
        </Box>
      );
    }

    return (
      <Box sx={{ mb: 2 }}>
        {/* 姓名输入框 */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <StyledTextField
              fullWidth
              placeholder="First Name"
              variant="outlined"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              error={paymentError && !firstName}
            />
          </Grid>
          <Grid item xs={6}>
            <StyledTextField
              fullWidth
              placeholder="Last Name"
              variant="outlined"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              error={paymentError && !lastName}
            />
          </Grid>
        </Grid>

        {/* 账户类型选择 */}
        <FormControl sx={{ mb: 2, width: '100%' }}>
          <RadioGroup
            row
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
            sx={{
              p: 2,
              border: '1px solid #E5E7EB',
              borderRadius: 2,
              '& .MuiFormControlLabel-root': {
                mr: 4
              }
            }}
          >
            <FormControlLabel 
              value="Checking" 
              control={
                <Radio 
                  sx={{
                    color: '#E5E7EB',
                    '&.Mui-checked': {
                      color: '#007AFF',
                    },
                  }}
                />
              } 
              label={
                <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                  Checking
                </Typography>
              }
            />
            <FormControlLabel 
              value="Saving" 
              control={
                <Radio 
                  sx={{
                    color: '#E5E7EB',
                    '&.Mui-checked': {
                      color: '#007AFF',
                    },
                  }}
                />
              } 
              label={
                <Typography sx={{ fontSize: 15, fontWeight: 500 }}>
                  Saving
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>

        {/* 账号和路由号码输入框 */}
        <StyledTextField
          fullWidth
          placeholder="Account Number"
          variant="outlined"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ''))}
          sx={{ mb: 2 }}
          error={paymentError && !accountNumber}
        />
        
        <StyledTextField
          fullWidth
          placeholder="Routing Number"
          variant="outlined"
          value={routingNumber}
          onChange={(e) => setRoutingNumber(e.target.value.replace(/\D/g, ''))}
          error={paymentError && !routingNumber}
        />
      </Box>
    );
  };

  // 修改支付处理函数
  const handleFortePayment = async () => {
    
    try {
      setIsProcessing(true);
      setPaymentError('');
      
      if (methodType === 'payCardBO') {
        // 验证表单
        if (!cardNumber || !cardholderName || !expiryDate || !cvv) {
          setPaymentError('Please fill in all payment information');
          setIsProcessing(false);
          return;
        }
        
        // 验证卡号格式
        const cleanCardNumber = cardNumber.replace(/\s/g, '');
        if (cleanCardNumber.length < 13 || cleanCardNumber.length > 16) {
          setPaymentError('Please enter a valid card number');
          setIsProcessing(false);
          return;
        }
        
        // 验证有效期格式
        if (!expiryDate.includes('/') || expiryDate.length !== 5) {
          setPaymentError('Please enter a valid expiration date (MM/YY)');
          setIsProcessing(false);
          return;
        }
        
        // 验证CVV格式
        if (cvv.length < 3) {
          setPaymentError('Please enter a valid CVV');
          setIsProcessing(false);
          return;
        }
      } else {
        // 验证电子支票表单
        if (!firstName || !lastName || !accountNumber || !routingNumber) {
          setPaymentError('Please fill in all payment information');
          setIsProcessing(false);
          return;
        }
      }
      
      // 准备支付数据
      const orderData = {
        couponTemplateId: couponTemplateListItem?.couponTemplateId || '',
        methodType: methodType === 'payCardBO' ? 1 : 2,
        ...(methodType === 'payCardBO' ? {
          payCardBO: {
            nameOnCard: cardholderName,
            cardType: "visa", // 可以根据卡号前缀判断卡类型
            accountNumber: cardNumber.replace(/\s/g, ''),
            expireMonth: parseInt(expiryDate.split('/')[0]),
            expireYear: parseInt('20' + expiryDate.split('/')[1]),
            cardVerificationValue: cvv
          }
        } : {
          payEcheckBO: {
            accountHolder: `${firstName} ${lastName}`,
            accountType: accountType,
            accountNumber: accountNumber,
            routingNumber: routingNumber
          }
        })
      };

      // 调用创建订单接口
      await createSimpleOrder(orderData, (result) => {
        let { code, data, msg } = result;
        
        if (code == 200) {
          // 支付成功，跳转到成功页面
          showMessage('Payment successful', 'success'); 
          setTimeout(() => {
            navigate('/campaigns/invitations/pay-success', { 
              state: { 
                couponTemplateListItem
              } 
            });
          }, 1500);
        } else {
          showMessage(msg, 'error');
          setPaymentError(msg.toLowerCase() || 'Failed to create order');
          // throw new Error('Failed to create order');
        }
      });

    } catch (error) {
      console.error('Payment error:', error);
      setPaymentError(error.message || 'An error occurred during payment processing. Please try again later.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F9FAFB' }}>

      {/* 导航栏 */}
      {
        !isInApp() && (
          <Box sx={{ 
            height: 44, 
            bgcolor: '#FFFFFF', 
            display: 'flex', 
            alignItems: 'center',
            borderBottom: '1px solid #F0F0F0'
          }}>
            <Box sx={{ width: '100%', display: 'flex', alignItems: 'center' }}>
              <IconButton 
                onClick={() => navigate(-1)}
                sx={{ color: 'black', ml: 1 }}
              >
                <ArrowBackIosNewIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <Typography 
                variant="h6" 
                sx={{ 
                  flexGrow: 1, 
                  textAlign: 'center', 
                  fontWeight: 600,
                  fontSize: 17,
                }}
              >
                Checkout
              </Typography>
              <Box sx={{ width: 40 }} /> {/* 平衡布局 */}
            </Box>
          </Box>
        )
      }

      <Container maxWidth="sm" sx={{ pt: 2, pb: 10 }}>
        {/* 订单摘要 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #F0F0F0' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
              Order Summary
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary" sx={{ fontSize: 15 }}>Coupon Value</Typography>
                <Typography fontWeight={500} sx={{ fontSize: 15 }}>${couponTemplateListItem?.facePrice}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary" sx={{ fontSize: 15 }}>Processing Fee</Typography>
                <Typography fontWeight={500} sx={{ fontSize: 15 }}>$0.00</Typography>
              </Box>
              <Divider />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography fontWeight={600} sx={{ fontSize: 15 }}>Total Amount</Typography>
                <Typography fontWeight={700} variant="h6" sx={{ fontSize: 18 }}>${couponTemplateListItem?.actualPrice}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* 支付详情 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #F0F0F0' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
              Payment Details
            </Typography>
            {renderPaymentForm()}
          </CardContent>
        </Card>

        {/* 安全支付 */}
        <Card sx={{ mb: 2, borderRadius: 2, boxShadow: 'none', border: '1px solid #F0F0F0' }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, fontSize: 18 }}>
              Secure Payment
            </Typography>
            
            <SecurityItem>
              <LockIcon sx={{ color: '#007AFF', mr: 2 }} />
              <Typography sx={{ fontSize: 15 }}>256-bit SSL encryption</Typography>
            </SecurityItem>
            
            <SecurityItem>
              <VerifiedUserIcon sx={{ color: '#007AFF', mr: 2 }} />
              <Typography sx={{ fontSize: 15 }}>PCI DSS compliant</Typography>
            </SecurityItem>
            
            <SecurityItem>
              <SecurityIcon sx={{ color: '#007AFF', mr: 2 }} />
              <Typography sx={{ fontSize: 15 }}>Fraud protection</Typography>
            </SecurityItem>
          </CardContent>
        </Card>

        {/* 条款说明 */}
        <Box sx={{ textAlign: 'center', mb: 2, px: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 13, lineHeight: 1.5 }}>
            By proceeding with the payment, you agree to our{' '}
            <Typography component="span" sx={{ color: '#007AFF', fontWeight: 500, fontSize: 13 }}>
              Terms of Service
            </Typography>{' '}
            and{' '}
            <Typography component="span" sx={{ color: '#007AFF', fontWeight: 500, fontSize: 13 }}>
              Privacy Policy
            </Typography>
            . The coupon is non-refundable once purchased.
          </Typography>
        </Box>

        {/* 显示支付错误信息 */}
        {paymentError && (
          <Box sx={{ 
            mb: 2, 
            p: 2, 
            bgcolor: '#FEF2F2', 
            borderRadius: 2,
            border: '1px solid #FEE2E2'
          }}>
            <Typography color="error" sx={{ fontSize: 14 }}>
              {paymentError}
            </Typography>
          </Box>
        )}

        {/* 底部按钮 */}
        <Button
          variant="contained"
          fullWidth
          size="large"
          onClick={handleFortePayment}
          disabled={isProcessing}
          sx={{
            bgcolor: '#000000',
            color: 'white',
            py: 1.5,
            borderRadius: 28,
            textTransform: 'none',
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            maxWidth: 'calc(100% - 32px)',
            fontWeight: 600,
            fontSize: 16,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            '&:hover': {
              bgcolor: '#333',
            },
          }}
        >
          {isProcessing ? 'Processing...' : `Pay $${couponTemplateListItem?.actualPrice}`}
        </Button>
      </Container>
    </Box>
  );
};

export default PayCard;
